/**
 * slideTransformWriteback.ts — put a redesigned slide back into the live deck.
 *
 * Office.js cannot set a slide's OOXML directly, so the redesigned slide is
 * delivered the same way Compare Slides re-inserts one: rebuild the original
 * package with the new shape tree, trim `<p:sldIdLst>` down to the single
 * target slide, and hand that base64 `.pptx` to `insertSlidesFromBase64`.
 * Keeping every other part of the zip intact preserves the layout, master,
 * theme and media relationships the slide depends on, with no fragile pruning.
 *
 * The redesigned slide is inserted immediately after the original, which is
 * then deleted — a replacement in place. Both steps address the slide by its
 * host id, so applying several slides in one run cannot be thrown off by the
 * positions shifting as earlier slides are replaced.
 */

import { strToU8, zipSync } from "fflate";
import { uint8ToBase64 } from "../../../utils/base64";
import { DeckStructureError, readPartText, type PackageFiles } from "./pptxDeckParts";
import { spliceShapeTree } from "./shapeTreeSplice";
import type { SlideWithId } from "./slideSelection";

const PRESENTATION_PART = "ppt/presentation.xml";
const SLD_ID_LST_RE = /<p:sldIdLst\b[^>]*>[\s\S]*?<\/p:sldIdLst>/;
const SLD_ID_LST_BODY_RE = /<p:sldIdLst\b[^>]*>([\s\S]*?)<\/p:sldIdLst>/;
const SLD_ID_RE = /<p:sldId\b[^>]*\/>/g;

/** Thrown when the redesigned slide cannot be written back to the deck. */
export class SlideWritebackError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SlideWritebackError";
  }
}

export interface TransformedSlidePackage {
  /** A base64 `.pptx` containing only the redesigned slide. */
  readonly base64: string;
}

/**
 * Build a single-slide `.pptx` from `files`, replacing the slide at
 * `slidePosition` with `newSlideXml`.
 *
 * The generic half of the write-back: keep every other part of the zip intact
 * and trim `<p:sldIdLst>` to the one target slide. Shared by both transform
 * modes — the freehand path passes a slide whose shape tree was spliced, the
 * template path one whose text bodies were filled from a mapping plan — so the
 * `sldIdLst` trimming exists once rather than per mode.
 *
 * `files` is not mutated: only the two rewritten parts are replaced, and every
 * other part is re-zipped by reference, so the caller can reuse the same
 * unzipped package across every slide in a run.
 *
 * @throws {DeckStructureError} when a required part is missing.
 */
export function buildSingleSlidePackage(
  files: PackageFiles,
  slidePartPath: string,
  slidePosition: number,
  newSlideXml: string,
): TransformedSlidePackage {
  const presentationXml = readPartText(files, PRESENTATION_PART);
  if (!presentationXml) {
    throw new DeckStructureError("This presentation is missing its ppt/presentation.xml part.");
  }

  const listBody = SLD_ID_LST_BODY_RE.exec(presentationXml)?.[1];
  if (listBody == null) throw new DeckStructureError("This presentation has no slide list.");

  const sldIds = listBody.match(SLD_ID_RE) ?? [];
  const targetSldId = sldIds[slidePosition];
  if (!targetSldId) {
    throw new DeckStructureError(`Slide ${slidePosition + 1} was not found in this presentation.`);
  }

  // Shallow copy: only the two rewritten parts are replaced, every other part
  // is re-zipped by reference.
  const packageFiles: PackageFiles = { ...files };
  packageFiles[slidePartPath] = strToU8(newSlideXml);
  packageFiles[PRESENTATION_PART] = strToU8(
    presentationXml.replace(SLD_ID_LST_RE, `<p:sldIdLst>${targetSldId}</p:sldIdLst>`),
  );

  return { base64: uint8ToBase64(zipSync(packageFiles)) };
}

/**
 * Build a single-slide `.pptx` from `files`, with the slide at `slidePosition`
 * carrying `shapeTreeXml` instead of its original shapes.
 *
 * The freehand (AI design) path: splice the redesigned shape tree into the
 * slide, then package that one slide.
 *
 * @throws {DeckStructureError} when a required part is missing.
 * @throws {ShapeTreeSpliceError} when the slide's shape tree cannot be replaced.
 */
export function buildTransformedSlidePackage(
  files: PackageFiles,
  slidePartPath: string,
  slidePosition: number,
  shapeTreeXml: string,
): TransformedSlidePackage {
  const slideXml = readPartText(files, slidePartPath);
  if (!slideXml) {
    throw new DeckStructureError(`Slide part "${slidePartPath}" is missing from this presentation.`);
  }

  return buildSingleSlidePackage(
    files,
    slidePartPath,
    slidePosition,
    spliceShapeTree(slideXml, shapeTreeXml),
  );
}

/**
 * Insert the single slide contained in `base64` immediately after the slide
 * identified by `slideId`, leaving the original in place.
 *
 * The original is deliberately kept so the user can compare the two and delete
 * whichever they don't want — a redesign is a suggestion, not an edit, and
 * silently replacing hand-made content is the more destructive default.
 *
 * @throws {SlideWritebackError} when the target slide is no longer in the deck,
 * or the host accepted nothing.
 */
export async function insertTransformedSlideAfter(slideId: string, base64: string): Promise<void> {
  await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();

    for (const slide of slides.items) (slide as unknown as SlideWithId).load("id");
    await context.sync();

    const countBefore = slides.items.length;
    const hasOriginal = slides.items.some((slide) => (slide as unknown as SlideWithId).id === slideId);
    if (!hasOriginal) {
      throw new SlideWritebackError(
        "The slide was changed or removed while it was being redesigned, so the result was not applied.",
      );
    }

    context.presentation.insertSlidesFromBase64(base64, {
      formatting: PowerPoint.InsertSlideFormatting.keepSourceFormatting,
      targetSlideId: slideId,
    });
    await context.sync();

    // Confirm the host actually took it: insertSlidesFromBase64 resolves without
    // a return value, so slide count is the only signal that anything landed.
    const afterInsert = context.presentation.slides;
    afterInsert.load("items");
    await context.sync();

    if (afterInsert.items.length <= countBefore) {
      throw new SlideWritebackError(
        "PowerPoint did not accept the redesigned slide, so nothing was added.",
      );
    }
  });
}
